import { memo, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_SLUG,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import config from 'constants/config'

interface Props {
  offer: App.Offer | App.BedbankOffer
  market: string
  relatedTravelItems?: Array<App.BreadcrumbItem>
}

function OfferGoogleBreadcrumbsStructuredData(props: Props) {
  const { offer, market, relatedTravelItems } = props
  const intl = useIntl()

  const toursLabel = intl.formatMessage({
    id: 'GB9K-QHgF5iPeKVDufH5',
    defaultMessage: 'Tours',
    description: 'Offer type label for tour offer breadcrumbs in structured data',
  })

  const hotelsLabel = intl.formatMessage({
    id: 'bgtP331qsQPWsZR7JQIV',
    defaultMessage: 'Hotels',
    description: 'Offer type label for hotel offer breadcrumbs in structured data',
  })

  const typeLookup = useMemo<Utils.PartialRecord<App.OfferType, { name: string, slug: string }>>(() => ({
    [OFFER_TYPE_TOUR]: {
      name: toursLabel,
      slug: OFFER_TYPE_TOUR_SLUG,
    },
    [OFFER_TYPE_HOTEL]: {
      name: hotelsLabel,
      slug: OFFER_TYPE_HOTEL_SLUG,
    },
    [OFFER_TYPE_LAST_MINUTE]: {
      name: hotelsLabel,
      slug: OFFER_TYPE_HOTEL_SLUG,
    },
    [OFFER_TYPE_ALWAYS_ON]: {
      name: hotelsLabel,
      slug: OFFER_TYPE_HOTEL_SLUG,
    },
    [OFFER_TYPE_BED_BANK]: {
      name: hotelsLabel,
      slug: OFFER_TYPE_HOTEL_SLUG,
    },
  }), [hotelsLabel, toursLabel])

  const structuredData = useMemo(() => (
    offer.type === OFFER_TYPE_BED_BANK && relatedTravelItems ? {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Luxury Escapes',
        item: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}`,
      }].concat(relatedTravelItems.map((item, index): any => ({
        '@type': 'ListItem',
        position: index + 2,
        name: item.text,
        item: item.url ? `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}` + '/' + market.toLowerCase() + item.url : undefined,
      }))),
    } : {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: config.title,
        item: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}`,
      }, {
        '@type': 'ListItem',
        position: 2,
        name: market.toLowerCase(),
        item: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}` + '/' + market.toLowerCase(),
      }, {
        '@type': 'ListItem',
        position: 3,
        name: typeLookup[offer.type]?.name,
        item: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}` + '/' + market.toLowerCase() + '/' + typeLookup[offer.type]?.slug,
      }, {
        '@type': 'ListItem',
        position: 4,
        name: offer.name,
      }],
    }
  ), [market, offer, relatedTravelItems, typeLookup])

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default memo(OfferGoogleBreadcrumbsStructuredData)
